import { useConfirm } from '@genial/components';
import { instanceOfErrorListResponse, Service, type ErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { useState } from 'react';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useRedeemTabsContext } from '../contexts/RedeemTabsContext';
import { errorImportRedeemSpreadsheetMessage, screenName } from '../utils/constants';

import { RedeemTabKeyOptions } from './tabs';

export const useSpreadsheetImport = () => {
  const { showToast, showToastFromError } = useToastCreator('toast-import-redeem-spreadsheet');
  const { goBack } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { confirm } = useConfirm();
  const { onTabPress: handleChangeTab } = useRedeemTabsContext();
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  const [isImportingSpreadsheet, startIsImportingSpreadsheetTransition] = useState(false);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<boolean>(false);

  const handleReset = () => {
    setFile(undefined);
    setFileError(false);
  };

  const handleError = () => {
    setFileError(true);
  };

  const handleFileChange = (selectedFile: File) => {
    setFileError(false);
    setFile(selectedFile);
  };

  const handleImport = async () => {
    if (!file) {
      return;
    }

    try {
      startIsImportingSpreadsheetTransition(true);

      const response = await Service.Basket.V1.postUploadFile({
        screenName,
        uploadBasketFileRequest: {
          fileContentType: file.type,
          fileExtension: file.name.split('.').pop(),
          fileExternalName: file.name,
        },
      });

      const signedPutUrl = response.data?.preSignedUrl;

      if (!signedPutUrl) {
        console.error('Erro na url: ', signedPutUrl);
        throw new Error('Url invalida');
      }

      await fetch(signedPutUrl, {
        method: 'PUT',
        body: file,
        mode: 'cors',
        credentials: 'same-origin',
      });

      confirm(
        {
          isMobile: !isDesktop,
          description:
            'Esta solicitação pode levar alguns minutos para ser processada.\nPor favor, aguarde até que o status seja atualizado para A REVISAR.',
          title: 'Atenção',
          icon: 'Warning',
          iconColor: 'feedback.attention.default',
          confirmText: 'Continuar',
          hideCancelButton: true,
        },
        () => handleChangeTab('my-requests' as RedeemTabKeyOptions),
        goBack
      );
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorImportRedeemSpreadsheetMessage);
    } finally {
      startIsImportingSpreadsheetTransition(false);
    }
  };

  const importButtonIsDisabled = fileError || !file || isImportingSpreadsheet;

  return {
    handleReset,
    handleError,
    handleFileChange,
    handleImport,
    importButtonIsDisabled,
    isImportingSpreadsheet,
  };
};
